import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Lock, ArrowLeft, ArrowRight } from 'lucide-react';

import api from '../../services/api';
import { formatDate } from '../../utils/Utils';
import usePerformanceSettings from '../../hooks/usePerformanceSettings';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import TextInput from '../../components/ui/TextInput';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import CheckboxInput from '../../components/ui/CheckboxInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tooltip from '../../components/ui/Tooltip';

const STEP_COUNT = 5;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// #70 — sihirbazın yüksekliği artık adımdan adıma DEĞİŞMİYOR (kullanıcı raporu:
// içerik kısa olan adımlarda modal küçülüp navigasyon barı zıplıyordu). Modal
// SABİT bir çerçeve: flex-column + `height` (viewport küçükse `max-height`
// devreye girer, önceki 88vh tavanı korunuyor) + `overflow: hidden` — yalnız
// ORTA bölüm (StepContent, aşağıda) kendi içinde kaydırılır; Header/
// StepIndicator/Footer bu kaydırmanın DIŞINDA sabit kalır (flex-shrink: 0).
const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 640px;
  max-width: 92vw;
  height: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 28px 28px 0;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 0 28px;
  margin-bottom: 24px;
`;

// #70 — tek kaydırılabilir bölge. `min-height: 0` flex çocuğunun içeriğe göre
// büyüyüp Modal'ı ittirmesini (flex item'ların varsayılan min-height:auto
// davranışı) engelleyen standart düzeltme — bu olmadan `flex:1` + `overflow-y:
// auto` sabit yükseklikte kaydırma üretmez, Modal yine içeriğe göre uzardı.
const StepContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 28px;
`;

const StepDot = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $active, $completed, theme }) =>
    $active ? theme.colors.primary.main : $completed ? '#16A34A' : '#E0E4E8'};
  color: ${({ $active, $completed }) => ($active || $completed) ? '#fff' : '#64748B'};
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ $completed }) => ($completed ? '#16A34A' : '#E0E4E8')};
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  > * { flex: 1; min-width: 0; }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const StepTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

const HintText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #EA580C;
  background: #FFF7ED;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

const InfoText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  background: #F1F5F9;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

// Faz E (Task 4) — sihirbaz Adım 2'nin tekil form GenericSelect'i çoklu satır
// editörüne genişledi (spec §4 "UI"). Kart deseni RatingScalesTab.js'in
// LevelCard'ıyla BİREBİR aynı (komşu idiom — ekle/çıkar satır editörü, aynı
// dosyada olmasa da bu projedeki tek emsal).
const FormRowsSection = styled.div`
  margin-bottom: 16px;
`;

const FormRowsSectionTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const FormRowCard = styled.div`
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  background: #FAFBFC;
`;

const FormRowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  > *:first-child { flex: 1; min-width: 0; }
`;

const DefaultBadge = styled.span`
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  margin-top: 6px;
  border-radius: 12px;
  background: rgba(2, 145, 239, 0.1);
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

const RemoveRowBtn = styled.button`
  flex-shrink: 0;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-top: 6px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #667;
  &:hover:not(:disabled) { border-color: #E53935; color: #E53935; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const RowHint = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #A0A8B4;
  margin-top: 4px;
`;

const ScaleOptionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #F1F5F9;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;

  &:last-child { border-bottom: none; }
`;

const SummaryLabel = styled.span`
  color: #64748B;
`;

const SummaryValue = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  text-align: right;
`;

// #70 — navigasyon barı artık Modal'ın sabit alt bandı (flex-shrink: 0,
// StepContent'in kaydırmasının DIŞINDA) — üstteki içerik ne kadar uzasa da
// "Önceki/Sonraki/Kaydet" hep aynı yerde durur. `border-top` kaydırılabilir
// içerikten görsel ayrım sağlıyor (üstte daha içerik olabileceğini ima eder).
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  padding: 16px 28px 24px;
  border-top: 1px solid #E0E4E8;
`;

const FooterRight = styled.div`
  display: flex;
  gap: 10px;
`;

const searchDepartments = async (term) => {
  const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/departments', { params });
  return (res.data?.data?.data || []).map((d) => ({ id: d.id, name: d.name }));
};

const searchUserGroups = async (term) => {
  const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/user-groups', { params });
  return (res.data?.data?.data || []).map((g) => ({ id: g.id, name: g.name }));
};

// ═══════ PMF-12 (Faz 2, 2026-09-11) — KAPSAM ÇİPLERİ BOŞ GELİYOR, TIKLAMAK SİLİYOR ═══════
//
// AsyncGenericSelect çipleri `selectedItems` state'inden çizer ve bu state YALNIZ
// `onGetById` ile doldurulur (bkz. o bileşendeki loadInitialData). onGetById
// verilmeyen çoklu seçicide `value` dolu olsa bile çip ÇİZİLMEZ.
//
// Sonuç yalnız kozmetik değil, VERİ KAYBI: İK kapsamlanmış bir taslak dönemi
// tekrar açtığında SIFIR çip görür, "eksik" sandığı departmanı listeden yeniden
// seçer — ama handleSelect çoklu modda `currentValues.includes(option.id)` ile
// TOGGLE yapar, yani seçili olanı SİLER. Görsel geri bildirim olmadığı için
// kullanıcı eklediğini sanıp daralmış kapsamı kaydeder.
//
// `searchDepartments`/`searchUserGroups` ile AYNI yanıt şekli; tekil GET'ler
// (v1/departments/{id}, v1/user-groups/{id}) zaten mevcut.
const getDepartmentById = async (id) => {
  if (!id) return null;
  const res = await api.get(`/v1/departments/${id}`);
  const d = res.data?.data;
  return d ? { id: d.id, name: d.name } : null;
};

const getUserGroupById = async (id) => {
  if (!id) return null;
  const res = await api.get(`/v1/user-groups/${id}`);
  const g = res.data?.data;
  return g ? { id: g.id, name: g.name } : null;
};

// export: PeriodWizard.test.js'in "computeIsDirty — pür fonksiyon" bloğu isDirty'nin
// hydrate edilmiş üç-alan dalını izole test edebilsin diye (Task 11 fix round 1, bulgu 1).
export const INITIAL_FORM = {
  name: '',
  startDate: '',
  endDate: '',
  plannedAnnouncementDate: '',
  evalWindowStart: '',
  evalWindowEnd: '',
  calibWindowStart: '',
  calibWindowEnd: '',
  systemAEnabled: true,
  systemBEnabled: false,
  // Faz E (Task 4) — tekil feedbackFormId yerine çoklu satır editörü: tek
  // boş satır = varsayılan (legacy tek-form davranışıyla aynı his, spec §4).
  // `targetDepartmentIds` boş = bu satır varsayılan form. `_key` yalnız React
  // listesi için istemci-yerel kimlik (BE'ye gönderilmez, bkz. buildPayload).
  feedbackForms: [{ _key: 'form-row-0', formId: null, targetDepartmentIds: [] }],
  scopeType: 'company',
  scopeTargetIds: [],
  // Faz A/Plan 2 (Task 11) — üçü de null başlar; "kullanıcı dokunmadı" durumunu
  // temsil eder. Ayarlar yüklenince tenant varsayımlarından bir kez hydrate
  // edilir (bkz. hydrate useEffect) — bkz. isDirty'nin bu üç alanı NULL yerine
  // hydrate edilmiş varsayıma göre kontrol etmesi (hydrate otomatik "kirletmesin").
  ratingScaleId: null,
  usesGoalWeights: null,
  discloseFinalLevel: null,
  // Faz B (spec 2026-08-07 §2.2, Task 6) — kalibrasyon oturumu ön-seçimi;
  // yukarıdaki üçle AYNI hydrate/isDirty desenine dahil edildi (dördüncü null
  // başlangıç alanı — bkz. hydrate useEffect + computeIsDirty).
  calibrationSessionEnabled: null,
};

// 5 adımlık kurulum tek yanlış backdrop tıklamasıyla kaybolmasın: form başlangıçtan
// sapmışsa (dirty) backdrop VE X kapatmadan önce onay ister; hiç dokunulmamışsa eskisi
// gibi doğrudan kapanır (bkz. bileşen içindeki `requestClose`).
//
// Task 11 — ratingScaleId/usesGoalWeights/discloseFinalLevel ayarlardan OTOMATİK hydrate
// edildiği için ham `INITIAL_FORM` (üçü de null) karşısında her zaman "farklı" görünürler;
// ayarlar yüklenir yüklenmez kullanıcı hiçbir şeye dokunmadan bile dirty=true'ya düşülürdü
// (backdrop'a tıklayınca gereksiz onay istemi). Bu üç alan INITIAL_FORM yerine settings'ten
// türeyen "hydrate edilmiş varsayım"a karşı kontrol edilir; kalan alanlar eskisi gibi
// INITIAL_FORM'a karşı.
//
// Fix round 1 (bulgu 1) — bu, bileşenin `isDirty` useMemo'sundan MODÜL SEVİYESİNE, saf/
// export edilmiş bir fonksiyona çıkarıldı: mantık settings'e göre çok-dallı olduğundan,
// bir sonraki düzenleme false-positive dirty'yi (ya da tersini) sessizce geri getirebilir.
// Saf fonksiyon PeriodWizard.test.js'in "computeIsDirty — pür fonksiyon" bloğunda TAM
// İZOLASYONLA (adım1'in doldurulmasından bağımsız, yalnız bu üç alan değiştirilerek) pinlenir
// — bileşeni uçtan uca render eden bir testte adım1 zaten dolu olacağından (Next butonu
// başka türlü aktif olmaz) `restForm` dalı erken true dönerdi ve asıl düzeltilen dal hiç
// izole edilemezdi.
// Faz J (spec 2026-08-08-pm-hijyen-faz-j §3/§4, J1, Task 2) — üçüncü, opsiyonel
// `initialForm` parametresi: EDİT modunda "başlangıç" INITIAL_FORM DEĞİL,
// hydrate edilmiş dönem verisidir (bkz. hydrateForm) — aksi halde açılışta
// hemen dirty=true'ya düşülür (her alan boş INITIAL_FORM'a karşı "farklı"
// görünür) ve kullanıcı hiçbir şeye dokunmadan backdrop'a tıklasa bile
// gereksiz "kaydedilmemiş değişiklik" onayı istenirdi. Parametre atlanırsa
// (create modu) davranış ESKİSİYLE BİT-EŞİT (varsayılan INITIAL_FORM +
// settings-fallback dalı, aşağıda DEĞİŞMEDİ).
export function computeIsDirty(form, settings, initialForm = INITIAL_FORM) {
  const {
    ratingScaleId, usesGoalWeights, discloseFinalLevel, calibrationSessionEnabled, ...restForm
  } = form;
  const restInitial = { ...initialForm };
  delete restInitial.ratingScaleId;
  delete restInitial.usesGoalWeights;
  delete restInitial.discloseFinalLevel;
  delete restInitial.calibrationSessionEnabled;
  if (JSON.stringify(restForm) !== JSON.stringify(restInitial)) return true;

  // Edit modu (initialForm INITIAL_FORM DEĞİL): dört alan da hydrate'ten
  // GELDİĞİ için (period DTO'su hiçbir alanı null bırakmaz — bkz. hydrateForm)
  // settings-fallback dalı burada ANLAMSIZ; doğrudan hydrate edilmiş
  // baseline'a karşı karşılaştırılır.
  if (initialForm !== INITIAL_FORM) {
    return ratingScaleId !== initialForm.ratingScaleId
      || usesGoalWeights !== initialForm.usesGoalWeights
      || discloseFinalLevel !== initialForm.discloseFinalLevel
      || calibrationSessionEnabled !== initialForm.calibrationSessionEnabled;
  }

  if (!settings) {
    return ratingScaleId !== null || usesGoalWeights !== null || discloseFinalLevel !== null
      || calibrationSessionEnabled !== null;
  }
  return ratingScaleId !== (settings.defaultRatingScaleId ?? null)
    || usesGoalWeights !== !!settings.goalWeightsEnabled
    || discloseFinalLevel !== !!settings.discloseFinalLevelDefault
    || calibrationSessionEnabled !== !!settings.calibrationSessionDefault;
}

// Faz J (Task 2, J1) — BE'nin ISO datetime string'ini (ör. "2026-07-01T00:00:00")
// DateTimePicker'ın beklediği "yyyy-MM-dd" biçimine indirger. BE her zaman
// tarihin gün bileşenini ilk 10 karakterde taşır (ISO 8601) — saat dilimi/saat
// bileşeni İGNORE edilir (mode="date" zaten yalnız günü gösterir).
function toDateInputValue(isoValue) {
  return isoValue ? String(isoValue).slice(0, 10) : '';
}

// Faz J (Task 2, J1) — GetById/ListAsync'in döndürdüğü PeriodFormSummaryDto[]'yi
// (`{formId, formName, targetDepartmentIds, isDefault}`) sihirbazın satır
// editörünün beklediği şekle (`{_key, formId, targetDepartmentIds}`) çevirir.
// Boşsa (Sistem B hiç açılmamış taslak) INITIAL_FORM'daki TEK boş satırla AYNI
// başlangıç durumuna düşer — "Sistem B'yi şimdi aç" akışı create'teki HİSSİN
// AYNISI olsun diye.
function hydrateFeedbackFormRows(periodForms) {
  if (!periodForms || periodForms.length === 0) {
    return [{ _key: 'form-row-0', formId: null, targetDepartmentIds: [] }];
  }
  return periodForms.map((row, idx) => ({
    _key: `form-row-hydrate-${idx}`,
    formId: row.formId,
    targetDepartmentIds: row.targetDepartmentIds || [],
  }));
}

// Faz J (Task 2, J1) — edit modunun tek giriş noktası: EvaluationPeriodResponse
// (liste satırı — GetById ile AYNI şekil, bkz. task-2-report.md §1) → sihirbaz
// form state'i. buildPayload'ın TAM TERSİ — hydrate↔payload simetrisi
// PeriodWizard.test.js'te doğrudan pinlenir.
export function hydrateForm(period) {
  return {
    name: period.name || '',
    startDate: toDateInputValue(period.startDate),
    endDate: toDateInputValue(period.endDate),
    plannedAnnouncementDate: toDateInputValue(period.plannedAnnouncementDate),
    evalWindowStart: toDateInputValue(period.evalWindowStart),
    evalWindowEnd: toDateInputValue(period.evalWindowEnd),
    calibWindowStart: toDateInputValue(period.calibWindowStart),
    calibWindowEnd: toDateInputValue(period.calibWindowEnd),
    systemAEnabled: !!period.systemAEnabled,
    systemBEnabled: !!period.systemBEnabled,
    feedbackForms: hydrateFeedbackFormRows(period.feedbackForms),
    scopeType: period.scopeType || 'company',
    scopeTargetIds: period.scopeTargetIds || [],
    ratingScaleId: period.ratingScaleId ?? null,
    usesGoalWeights: !!period.usesGoalWeights,
    discloseFinalLevel: !!period.discloseFinalLevel,
    calibrationSessionEnabled: !!period.calibrationSessionEnabled,
  };
}

// PRD PM §3.2 — 5 adımlı açılış sihirbazı. Her açılışta EvaluationPeriodsPage
// bu bileşeni yeniden mount eder (bkz. çağıran taraf), bu yüzden state
// sıfırlama burada gerekmiyor.
//
// Faz J (spec 2026-08-08-pm-hijyen-faz-j §3/§4, J1, Task 2) — `period` prop'u
// DOLUYSA bileşen EDİT moduna geçer: `form` state'i INITIAL_FORM yerine
// hydrateForm(period)'dan başlar, başlık "Dönemi Düzenle" olur, kaydet aksiyonu
// `create`+opsiyonel `activate` YERİNE tek bir `update(period.id, payload)`
// çağrısıdır. CREATE YOLU BİT-EŞİT KALIR — `period` prop'u boşsa (varsayılan)
// aşağıdaki HİÇBİR dal farklı davranmaz.
export default function PeriodWizard({
  onClose, create, update, activate, period = null,
}) {
  const { t, i18n } = useTranslation();
  const isEdit = !!period;

  const [step, setStep] = useState(1);
  // Lazy initializer TAM OLARAK BİR KEZ çalışır (React garantisi) — bileşen
  // her açılışta yeniden mount edildiği için (yorum yukarıda) bu, edit modunda
  // `period`in DEĞİŞMEYECEK sabit bir "ilk anlık görüntü"sünü de verir; aynı
  // anlık görüntü aşağıda isDirty'nin baseline'ı olarak TEKRAR kullanılır
  // (iki ayrı hydrate çağrısı => iki farklı `_key` seti => referans eşitsizliği
  // RİSKİNDEN kaçınmak için tek sefer hesaplanır).
  const [initialForm] = useState(() => (isEdit ? hydrateForm(period) : INITIAL_FORM));
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [scales, setScales] = useState([]);
  const [scalesLoading, setScalesLoading] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const { settings } = usePerformanceSettings();

  useEffect(() => {
    setFormsLoading(true);
    api.get('/v1/feedback-forms', { params: { pageSize: 200 } })
      .then((res) => {
        const items = res.data?.data?.data || [];
        setForms(items.map((f) => ({ id: f.id, name: f.name })));
      })
      .finally(() => setFormsLoading(false));
  }, []);

  // GET v1/rating-scales SAYFALANMAZ (bkz. RatingScaleDataAccess.ListAsync —
  // RatingScalesTab.js'in aynı deseni) — tenant'ın tüm aktif skalalarını tek
  // seferde döner, `res.data.data` doğrudan dizi (feedback-forms'un aksine
  // `.data.data` iç içe DEĞİL).
  useEffect(() => {
    setScalesLoading(true);
    api.get('/v1/rating-scales')
      .then((res) => {
        setScales(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .finally(() => setScalesLoading(false));
  }, []);

  // Faz A/Plan 2 (Task 11) — sihirbaz ön-seçim varsayılanlarını tenant
  // ayarlarından BİR KEZ hydrate eder: alan hâlâ null'sa (kullanıcı henüz
  // dokunmadıysa) settings'teki değeri yazar; kullanıcı elle değiştirdiyse
  // (artık null değil) bir daha ÜZERİNE YAZMAZ. `settings` `usePerformanceSettings`
  // önbelleğinden geldiği için birden çok mount'ta stabil referans/değer olur.
  useEffect(() => {
    if (!settings) return;
    setForm((f) => (
      f.ratingScaleId === null && f.usesGoalWeights === null && f.discloseFinalLevel === null
        && f.calibrationSessionEnabled === null
        ? {
          ...f,
          ratingScaleId: settings.defaultRatingScaleId ?? null,
          usesGoalWeights: !!settings.goalWeightsEnabled,
          discloseFinalLevel: !!settings.discloseFinalLevelDefault,
          // Faz B (Task 6) — dördüncü hydrate alanı, AYNI "bir kez, dokunulmadıysa" kapısı.
          calibrationSessionEnabled: !!settings.calibrationSessionDefault,
        }
        : f
    ));
  }, [settings]);

  const setField = useCallback((name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const scopeTypeOptions = useMemo(() => ([
    { id: 'company', name: t('performance.period.scope.company') },
    { id: 'department', name: t('performance.period.scope.department') },
    { id: 'group', name: t('performance.period.scope.group') },
  ]), [t]);

  // --- Adım 1: tarih sırası FE kontrolü (BE mesajına bırakılmaz) ---
  //
  // UI düzeltme demeti #66 (2026-08-09, kontrolör kararı) — EvalWindow'un
  // "dönem içinde" (evalWindowEnd <= endDate) zorunluluğu KALDIRILDI (bkz.
  // EvaluationPeriodDataAccess.ValidateCreateAsync yorumu — BE ile BİREBİR
  // AYNI kural): "dönem 31 Aralık'ta biter, puanlama 1-15 Ocak" gibi bir
  // desen artık FE'de de İleri'yi engellemez. Yalnız KORUNAN kurallar:
  // pencere dönem BAŞLAMADAN açılamaz (evalWindowStart >= startDate) ve
  // kendi içinde sıralı (evalWindowStart < evalWindowEnd).
  const step1DateOrderInvalid = useMemo(() => {
    if (!form.startDate || !form.endDate || !form.evalWindowStart || !form.evalWindowEnd) return false;
    if (form.startDate >= form.endDate) return true;
    if (form.evalWindowStart < form.startDate) return true;
    if (form.evalWindowStart >= form.evalWindowEnd) return true;
    return false;
  }, [form.startDate, form.endDate, form.evalWindowStart, form.evalWindowEnd]);

  // P1-C — planlanan ilan tarihi (opsiyonel) EndDate'ten ÖNCE olamaz (aynı gün
  // kabul — BE .Date karşılaştırmasıyla birebir; sonuçlar dönem bitmeden ilan
  // edilmez). ISO 'YYYY-MM-DD' string karşılaştırması yeterli.
  const announcementDateInvalid = useMemo(() => {
    if (!form.plannedAnnouncementDate || !form.endDate) return false;
    return form.plannedAnnouncementDate < form.endDate;
  }, [form.plannedAnnouncementDate, form.endDate]);

  const step1Valid = !!(form.name.trim() && form.startDate && form.endDate
    && form.evalWindowStart && form.evalWindowEnd && !step1DateOrderInvalid && !announcementDateInvalid);

  // Faz E (Task 4) — her satırda form seçilmiş olmalı (satır sayısı≥1); bu,
  // "her satırın bir formu olmalı" temel kontrolüdür. "Tam bir varsayılan"
  // ve "departman çakışması" kuralları BİLEREK burada YOK — spec/brief bunları
  // istemci-ipucu düzeyinde tutar, asıl kapı BE aktivasyon doğrulaması
  // (defaultFormRequired/duplicateDepartmentTarget, task-1-report.md §1);
  // taslak kaydında/Next'te bloklamıyoruz.
  const feedbackFormsComplete = useMemo(
    () => form.feedbackForms.length > 0 && form.feedbackForms.every((row) => !!row.formId),
    [form.feedbackForms],
  );

  // İstemci-ipucu (asıl kapı BE aktivasyonu): tam bir varsayılan (departmansız)
  // satır uyarısı.
  const defaultFormRowCount = useMemo(
    () => form.feedbackForms.filter((row) => !row.targetDepartmentIds || row.targetDepartmentIds.length === 0).length,
    [form.feedbackForms],
  );

  // İstemci-ipucu: aynı departman birden çok satırda hedeflenmiş mi.
  const duplicateDepartmentTargeted = useMemo(() => {
    const seen = new Set();
    for (const row of form.feedbackForms) {
      for (const depId of (row.targetDepartmentIds || [])) {
        if (seen.has(depId)) return true;
        seen.add(depId);
      }
    }
    return false;
  }, [form.feedbackForms]);

  const addFeedbackFormRow = useCallback(() => {
    setForm((f) => ({
      ...f,
      feedbackForms: [
        ...f.feedbackForms,
        { _key: `form-row-${Date.now()}-${f.feedbackForms.length}`, formId: null, targetDepartmentIds: [] },
      ],
    }));
  }, []);

  const removeFeedbackFormRow = useCallback((key) => {
    setForm((f) => (
      f.feedbackForms.length <= 1
        ? f
        : { ...f, feedbackForms: f.feedbackForms.filter((row) => row._key !== key) }
    ));
  }, []);

  const updateFeedbackFormRow = useCallback((key, patch) => {
    setForm((f) => ({
      ...f,
      feedbackForms: f.feedbackForms.map((row) => (row._key === key ? { ...row, ...patch } : row)),
    }));
  }, []);

  // --- Adım 2: en az bir sistem; Sistem B ise form VE puan skalası zorunlu ---
  const step2Valid = (form.systemAEnabled || form.systemBEnabled)
    && (!form.systemBEnabled || feedbackFormsComplete)
    && (!form.systemBEnabled || !!form.ratingScaleId);

  // --- Adım 3: company dışı kapsamda en az bir hedef ---
  const step3Valid = form.scopeType === 'company' || form.scopeTargetIds.length > 0;

  // --- Adım 4: kalibrasyon penceresi eval penceresinden sonra ve sıralı ---
  const step4DateOrderInvalid = useMemo(() => {
    if (!form.calibWindowStart || !form.calibWindowEnd) return false;
    if (form.calibWindowStart < form.evalWindowEnd) return true;
    if (form.calibWindowStart >= form.calibWindowEnd) return true;
    return false;
  }, [form.calibWindowStart, form.calibWindowEnd, form.evalWindowEnd]);

  const step4Valid = !!(form.calibWindowStart && form.calibWindowEnd && !step4DateOrderInvalid);

  const canGoNext = useMemo(() => {
    if (step === 1) return step1Valid;
    if (step === 2) return step2Valid;
    if (step === 3) return step3Valid;
    if (step === 4) return step4Valid;
    return true;
  }, [step, step1Valid, step2Valid, step3Valid, step4Valid]);

  const buildPayload = useCallback(() => {
    // Faz E (Task 4) — satırlardan BE'nin `feedbackForms` şeklini üret (task-1
    // report §1: `{formId, targetDepartmentIds?}[]`); `_key` istemci-yerel,
    // asla gönderilmez. Boş satır (formId yok) zaten step2Valid ile Next'te
    // engellenir ama burada da savunmacı filtrelenir.
    const feedbackForms = form.systemBEnabled
      ? form.feedbackForms
        .filter((row) => !!row.formId)
        .map((row) => ({
          formId: row.formId,
          targetDepartmentIds: (row.targetDepartmentIds && row.targetDepartmentIds.length > 0)
            ? row.targetDepartmentIds
            : null,
        }))
      : [];
    // BE geriye-uyum kısayolu: `feedbackForms` doluysa BE onu kullanır,
    // `feedbackFormId` YOK SAYILIR (task-1-report.md §1) — yine de eski
    // istemci/rapor beklentisiyle simetri için legacy alanı varsayılan
    // (departmansız) satırın formId'siyle dolduruyoruz.
    const defaultFeedbackFormRow = feedbackForms.find((row) => !row.targetDepartmentIds) || feedbackForms[0] || null;

    return {
      name: form.name.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      evalWindowStart: form.evalWindowStart,
      evalWindowEnd: form.evalWindowEnd,
      calibWindowStart: form.calibWindowStart,
      calibWindowEnd: form.calibWindowEnd,
      systemAEnabled: form.systemAEnabled,
      systemBEnabled: form.systemBEnabled,
      scopeType: form.scopeType,
      scopeTargetIds: form.scopeType === 'company' ? [] : form.scopeTargetIds,
      feedbackForms,
      feedbackFormId: defaultFeedbackFormRow?.formId ?? null,
      plannedAnnouncementDate: form.plannedAnnouncementDate || null,
      // Final review #4 (2026-08-06) — spec §8 "Kenar Durumları": Sistem A-only dönemde
      // RatingScaleId null olmalı (hiçbir yerde seviye üretilmez, PIP yalnız manuel).
      // BE (EvaluationPeriodDataAccess.CreateAsync) create anında req.RatingScaleId'i
      // koşulsuz saklar — A-only dönem için nullama BE'de YOK (yalnız ActivateAsync'te
      // SystemBEnabled dalında doğrulanır/kilitlenir) — bu yüzden FE feedbackFormId ile
      // AYNI desende, systemBEnabled değilse skalayı göndermez.
      ratingScaleId: form.systemBEnabled ? form.ratingScaleId : null,
      usesGoalWeights: form.usesGoalWeights,
      discloseFinalLevel: form.discloseFinalLevel,
      // Faz B (spec 2026-08-07 §2.2, Task 6) — kalibrasyon oturumu, Sistem B'ye
      // GÖRSEL olarak yakın dursa da (spec: "kalibrasyon SystemB akışının
      // parçası") sihirbazda BAĞIMSIZ bir toggle; Plan 5 F4 dersiyle aynı
      // sadeleştirme — systemBEnabled'a bakılmaksızın HER ZAMAN gönderilir (BE
      // create ucu bool? kabul eder, null→tenant varsayımı snapshot'ı — burada
      // hydrate zaten null'ı boolean'a çevirdiği için pratikte hep boolean gider).
      calibrationSessionEnabled: form.calibrationSessionEnabled,
    };
  }, [form]);

  const handleSubmit = useCallback(async (startImmediately) => {
    setSaving(true);
    // Faz J (Task 2, J1) — edit modu: create/activate YERİNE tek bir PUT.
    // startImmediately edit modunda ASLA true gelmez (footer tek "Kaydet"
    // butonu sunar, bkz. JSX) ama savunmacı olarak burada da göz ardı edilir.
    if (isEdit) {
      const ok = await update(period.id, buildPayload());
      if (ok) {
        onClose();
        return;
      }
      setSaving(false);
      return;
    }

    const newId = await create(buildPayload());
    if (newId) {
      if (startImmediately && typeof newId === 'string') {
        await activate(newId);
      }
      onClose();
      return;
    }
    setSaving(false);
  }, [isEdit, update, period, create, activate, buildPayload, onClose]);

  // Faz E (Task 4) — Adım 5 özeti artık çoklu satırı yansıtır: varsayılan
  // (departmansız) satırın form adı + varsa ek satır sayısı.
  const defaultFeedbackFormName = useMemo(() => {
    const defaultRow = form.feedbackForms.find(
      (row) => !row.targetDepartmentIds || row.targetDepartmentIds.length === 0,
    );
    return forms.find((f) => f.id === defaultRow?.formId)?.name || '-';
  }, [forms, form.feedbackForms]);

  const additionalFeedbackFormsCount = form.feedbackForms.length - 1;

  const selectedScaleName = useMemo(
    () => scales.find((s) => s.id === form.ratingScaleId)?.name || '-',
    [scales, form.ratingScaleId],
  );

  const renderScaleOption = useCallback((opt) => (
    <ScaleOptionLabel>
      {opt.name}
      {opt.locked && (
        <span title={t('performance.ratingScale.lockedBadge')}>
          <Lock size={12} />
        </span>
      )}
    </ScaleOptionLabel>
  ), [t]);

  // Bkz. modül seviyesindeki `computeIsDirty` yorumu (Task 11 + fix round 1, bulgu 1) —
  // mantık orada saf fonksiyon olarak yaşıyor, izole test edilebiliyor.
  const isDirty = useMemo(() => computeIsDirty(form, settings, initialForm), [form, settings, initialForm]);

  const requestClose = useCallback(() => {
    if (saving) return;
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  }, [saving, isDirty, onClose]);

  return (
    <Overlay onClick={requestClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog
          isOpen={closeConfirmOpen}
          title={t('common.unsavedChangesTitle')}
          message={t('common.unsavedChangesConfirm')}
          onConfirm={onClose}
          onCancel={() => setCloseConfirmOpen(false)}
        />
        <ModalHeader>
          <Title>{isEdit ? t('performance.period.editTitle') : t('performance.period.new')}</Title>
          <CloseBtn onClick={requestClose} disabled={saving}><X size={18} /></CloseBtn>
        </ModalHeader>

        <StepIndicator>
          {Array.from({ length: STEP_COUNT }).map((_, idx) => {
            const num = idx + 1;
            return (
              <React.Fragment key={num}>
                <StepDot $active={step === num} $completed={step > num}>{num}</StepDot>
                {num < STEP_COUNT && <StepLine $completed={step > num} />}
              </React.Fragment>
            );
          })}
        </StepIndicator>

        <StepContent>
          {step === 1 && (
            <>
              <StepTitle>{t('performance.period.step1')}</StepTitle>
              <Field>
                <TextInput
                  label={t('performance.period.name')}
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  required
                />
              </Field>
              <Row>
                <DateTimePicker
                  label={t('performance.period.startDate')}
                  mode="date"
                  value={form.startDate}
                  onChange={(v) => setField('startDate', v || '')}
                  required
                />
                <DateTimePicker
                  label={t('performance.period.endDate')}
                  mode="date"
                  value={form.endDate}
                  onChange={(v) => setField('endDate', v || '')}
                  required
                />
              </Row>
              <Row>
                <DateTimePicker
                  label={t('performance.period.evalWindowStart')}
                  mode="date"
                  value={form.evalWindowStart}
                  onChange={(v) => setField('evalWindowStart', v || '')}
                  required
                />
                <DateTimePicker
                  label={t('performance.period.evalWindowEnd')}
                  mode="date"
                  value={form.evalWindowEnd}
                  onChange={(v) => setField('evalWindowEnd', v || '')}
                  required
                />
              </Row>
              <Field>
                <DateTimePicker
                  label={t('performance.period.plannedAnnouncementDate')}
                  mode="date"
                  value={form.plannedAnnouncementDate}
                  onChange={(v) => setField('plannedAnnouncementDate', v || '')}
                />
              </Field>
              {step1DateOrderInvalid && <HintText>{t('performance.period.dateOrderHint')}</HintText>}
              {announcementDateInvalid && <HintText>{t('performance.period.announcementDateHint')}</HintText>}
            </>
          )}

          {step === 2 && (
            <>
              <StepTitle>{t('performance.period.step2')}</StepTitle>
              <Field>
                <CheckboxInput
                  label={t('performance.period.systemA')}
                  checked={form.systemAEnabled}
                  onChange={(v) => setField('systemAEnabled', v)}
                />
              </Field>
              {form.systemAEnabled && (
                <Field>
                  <CheckboxInput
                    label={t('performance.period.usesGoalWeights')}
                    checked={!!form.usesGoalWeights}
                    onChange={(v) => setField('usesGoalWeights', v)}
                  />
                </Field>
              )}
              <Field>
                <CheckboxInput
                  label={t('performance.period.systemB')}
                  checked={form.systemBEnabled}
                  onChange={(v) => setField('systemBEnabled', v)}
                />
              </Field>
              {!form.systemAEnabled && !form.systemBEnabled && (
                <HintText>{t('performance.period.systemRequiredHint')}</HintText>
              )}
              {form.systemBEnabled && (
                <>
                  {!formsLoading && forms.length === 0 && (
                    <InfoText>{t('performance.period.noFormsHint')}</InfoText>
                  )}
                  {!scalesLoading && scales.length === 0 && (
                    <InfoText>{t('performance.period.noScalesHint')}</InfoText>
                  )}
                  {forms.length > 0 && (
                    <FormRowsSection>
                      <FormRowsSectionTitle>{t('periodForms.sectionTitle')}</FormRowsSectionTitle>
                      {form.feedbackForms.map((row, idx) => {
                        const isDefaultRow = !row.targetDepartmentIds || row.targetDepartmentIds.length === 0;
                        return (
                          <FormRowCard key={row._key} data-testid={`period-form-row-${idx}`}>
                            <FormRowHeader>
                              <GenericSelect
                                label={t('periodForms.formLabel')}
                                options={forms}
                                value={row.formId}
                                onChange={(v) => updateFeedbackFormRow(row._key, { formId: v })}
                                idKey="id"
                                labelKey="name"
                                required
                              />
                              {isDefaultRow && <DefaultBadge>{t('periodForms.defaultBadge')}</DefaultBadge>}
                              <RemoveRowBtn
                                type="button"
                                onClick={() => removeFeedbackFormRow(row._key)}
                                disabled={form.feedbackForms.length <= 1}
                                title={t('periodForms.removeRow')}
                              >
                                <X size={14} />
                              </RemoveRowBtn>
                            </FormRowHeader>
                            <AsyncGenericSelect
                              label={t('periodForms.targetDepartmentsLabel')}
                              value={row.targetDepartmentIds}
                              onChange={(v) => updateFeedbackFormRow(row._key, { targetDepartmentIds: v || [] })}
                              onSearch={searchDepartments}
                              onGetById={getDepartmentById}
                              isMulti
                            />
                            <RowHint>{t('periodForms.targetDepartmentsHint')}</RowHint>
                          </FormRowCard>
                        );
                      })}
                      <Button type="button" variant="outline" onClick={addFeedbackFormRow}>
                        + {t('periodForms.addRow')}
                      </Button>
                      {defaultFormRowCount !== 1 && <HintText>{t('periodForms.defaultRowHint')}</HintText>}
                      {duplicateDepartmentTargeted && <HintText>{t('periodForms.duplicateDepartmentHint')}</HintText>}
                    </FormRowsSection>
                  )}
                  {scales.length > 0 && (
                    <Field>
                      <GenericSelect
                        label={t('performance.period.ratingScale')}
                        options={scales}
                        value={form.ratingScaleId}
                        onChange={(v) => setField('ratingScaleId', v)}
                        idKey="id"
                        labelKey="name"
                        renderOption={renderScaleOption}
                        required
                      />
                    </Field>
                  )}
                </>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <StepTitle>{t('performance.period.step3')}</StepTitle>
              <Field>
                <GenericSelect
                  label={t('performance.period.scopeType')}
                  options={scopeTypeOptions}
                  value={form.scopeType}
                  onChange={(v) => setForm((f) => ({ ...f, scopeType: v, scopeTargetIds: [] }))}
                  idKey="id"
                  labelKey="name"
                />
              </Field>
              {form.scopeType === 'department' && (
                <Field>
                  <AsyncGenericSelect
                    label={t('performance.period.scopeDepartments')}
                    value={form.scopeTargetIds}
                    onChange={(v) => setField('scopeTargetIds', v || [])}
                    onSearch={searchDepartments}
                    onGetById={getDepartmentById}
                    isMulti
                    required
                  />
                </Field>
              )}
              {form.scopeType === 'group' && (
                <Field>
                  <AsyncGenericSelect
                    label={t('performance.period.scopeGroups')}
                    value={form.scopeTargetIds}
                    onChange={(v) => setField('scopeTargetIds', v || [])}
                    onSearch={searchUserGroups}
                    onGetById={getUserGroupById}
                    isMulti
                    required
                  />
                </Field>
              )}
              {form.scopeType !== 'company' && form.scopeTargetIds.length === 0 && (
                <HintText>{t('performance.period.scopeTargetsRequiredHint')}</HintText>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <StepTitle>{t('performance.period.step4')}</StepTitle>
              <Row>
                <DateTimePicker
                  label={t('performance.period.calibWindowStart')}
                  mode="date"
                  value={form.calibWindowStart}
                  onChange={(v) => setField('calibWindowStart', v || '')}
                  required
                />
                <DateTimePicker
                  label={t('performance.period.calibWindowEnd')}
                  mode="date"
                  value={form.calibWindowEnd}
                  onChange={(v) => setField('calibWindowEnd', v || '')}
                  required
                />
              </Row>
              {step4DateOrderInvalid && <HintText>{t('performance.period.dateOrderHint')}</HintText>}

              <StepTitle>{t('performance.period.disclosureSection')}</StepTitle>
              <Field>
                <CheckboxInput
                  label={t('performance.period.discloseFinalLevel')}
                  checked={!!form.discloseFinalLevel}
                  onChange={(v) => setField('discloseFinalLevel', v)}
                />
              </Field>
              {/* Faz B (spec 2026-08-07 §2.2, Task 6) — Sonuç Tebliği'nin yanında,
                  ön-değeri usePerformanceSettings'ten (calibrationSessionDefault). */}
              <Field>
                <CheckboxInput
                  label={t('performance.period.calibrationSessionEnabled')}
                  checked={!!form.calibrationSessionEnabled}
                  onChange={(v) => setField('calibrationSessionEnabled', v)}
                />
              </Field>
            </>
          )}

          {step === 5 && (
            <>
              <StepTitle>{t('performance.period.summaryTitle')}</StepTitle>
              <SummaryRow>
                <SummaryLabel>{t('performance.period.name')}</SummaryLabel>
                <SummaryValue>{form.name}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>{t('performance.period.startDate')} — {t('performance.period.endDate')}</SummaryLabel>
                <SummaryValue>{formatDate(form.startDate, i18n.language)} — {formatDate(form.endDate, i18n.language)}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>{t('performance.period.evalWindowStart')}</SummaryLabel>
                <SummaryValue>{formatDate(form.evalWindowStart, i18n.language)} — {formatDate(form.evalWindowEnd, i18n.language)}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>{t('performance.period.calibWindowStart')}</SummaryLabel>
                <SummaryValue>{formatDate(form.calibWindowStart, i18n.language)} — {formatDate(form.calibWindowEnd, i18n.language)}</SummaryValue>
              </SummaryRow>
              {form.plannedAnnouncementDate && (
                <SummaryRow>
                  <SummaryLabel>{t('performance.period.plannedAnnouncementDate')}</SummaryLabel>
                  <SummaryValue>{formatDate(form.plannedAnnouncementDate, i18n.language)}</SummaryValue>
                </SummaryRow>
              )}
              <SummaryRow>
                <SummaryLabel>{t('performance.period.systems')}</SummaryLabel>
                <SummaryValue>
                  {[form.systemAEnabled && 'A', form.systemBEnabled && 'B'].filter(Boolean).join(' + ') || '-'}
                </SummaryValue>
              </SummaryRow>
              {form.systemBEnabled && (
                <SummaryRow>
                  <SummaryLabel>{t('performance.period.feedbackForm')}</SummaryLabel>
                  <SummaryValue>
                    {defaultFeedbackFormName}
                    {additionalFeedbackFormsCount > 0 ? ` (+${additionalFeedbackFormsCount})` : ''}
                  </SummaryValue>
                </SummaryRow>
              )}
              {form.systemBEnabled && (
                <SummaryRow>
                  <SummaryLabel>{t('performance.period.ratingScale')}</SummaryLabel>
                  <SummaryValue>{selectedScaleName}</SummaryValue>
                </SummaryRow>
              )}
              {form.systemAEnabled && (
                <SummaryRow>
                  <SummaryLabel>{t('performance.period.usesGoalWeights')}</SummaryLabel>
                  <SummaryValue>{form.usesGoalWeights ? t('common.yes') : t('common.no')}</SummaryValue>
                </SummaryRow>
              )}
              <SummaryRow>
                <SummaryLabel>{t('performance.period.discloseFinalLevel')}</SummaryLabel>
                <SummaryValue>{form.discloseFinalLevel ? t('common.yes') : t('common.no')}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>{t('performance.period.calibrationSessionEnabled')}</SummaryLabel>
                <SummaryValue>{form.calibrationSessionEnabled ? t('common.yes') : t('common.no')}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>{t('performance.period.scopeType')}</SummaryLabel>
                <SummaryValue>
                  {t(`performance.period.scope.${form.scopeType}`)}
                  {form.scopeType !== 'company' ? ` (${form.scopeTargetIds.length})` : ''}
                </SummaryValue>
              </SummaryRow>
            </>
          )}
        </StepContent>

        <Footer>
          {/* #72 — T2'nin yerel PrevIconBtn/NextIconBtn/PrimaryCta/SecondaryCta
              ailesi kaldırıldı, paylaşılan IconButton/Button'a bağlandı
              (t2-report.md §4 sözleşmesinin GENELLEŞTİRİLMİŞ hedefi — CSS
              tarifi artık BURADAN değil components/ui/IconButton.js +
              Button.js'ten geliyor, görünüm BİT-EŞİT). */}
          <Tooltip text={t('common.previous')}>
            <IconButton
              type="button"
              data-role="wizard-prev-btn"
              aria-label={t('common.previous')}
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 1 || saving}
            >
              <ArrowLeft size={18} />
            </IconButton>
          </Tooltip>
          {step < STEP_COUNT ? (
            <Tooltip text={t('common.next')}>
              <IconButton
                variant="gradient"
                type="button"
                data-role="wizard-next-btn"
                aria-label={t('common.next')}
                onClick={() => setStep((s) => s + 1)}
                disabled={!canGoNext || saving}
              >
                <ArrowRight size={18} />
              </IconButton>
            </Tooltip>
          ) : isEdit ? (
            // Faz J (Task 2, J1) — edit modunda TEK aksiyon: Kaydet (PUT).
            // "Şimdi Başlat" burada YOK — aktifleştirme zaten dönem listesinin
            // kendi "Aktive Et" aksiyonundan ayrıca yapılabilir (spec: PUT
            // durumu DEĞİŞTİRMEZ).
            <FooterRight>
              <Button type="button" data-role="wizard-save-btn" onClick={() => handleSubmit(false)} disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </FooterRight>
          ) : (
            <FooterRight>
              <Button variant="outline" type="button" data-role="wizard-save-draft-btn" onClick={() => handleSubmit(false)} disabled={saving}>
                {saving ? t('common.saving') : t('performance.period.saveDraft')}
              </Button>
              <Button type="button" data-role="wizard-start-now-btn" onClick={() => handleSubmit(true)} disabled={saving}>
                {saving ? t('common.saving') : t('performance.period.startNow')}
              </Button>
            </FooterRight>
          )}
        </Footer>
      </Modal>
    </Overlay>
  );
}
